import { action, computed, observable, runInAction, makeObservable, toJS } from 'mobx';
import { message } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { get, getAuthToken, post, requests, patch } from '@partsbadger/utils';
import {
    buildPendingEmbeddedExtraction,
    dequeueEmbeddedExtraction,
    enqueueEmbeddedExtraction,
    hasExtractableEmbedded3d,
    PendingEmbeddedExtraction,
} from '@partsbadger/library';

import {
    AccountProps,
    CoatingsProps,
    ContactDetailProps,
    ContactDocumentsProps,
    FinishesProps,
    HoleTorelances,
    IRequirement,
    MaterialProps,
    PartTolerances,
    QuoteLineItemProps,
    QuoteProps,
    UserProps,
} from '@partsbadger/types';
import { ProductProps } from '@partsbadger/types/lib/QuoteInterfaces';
import { AcquisitionSourceEC, AcquisitionSourceNC } from '../constants/acquisition_source';
import { LeadStore } from './LeadStore';
import { createRequestCache } from '../utils/requestCache';
import { getLineItemErrorFields } from '../utils/partStatus';
import { BulkResult, BulkSpecs, buildSpecsPayload } from '../utils/bulkSpecs';
import { buildQuantitiesPayload, QuantityMode, QuantityResult } from '../utils/bulkQuantities';
import { hasPendingWork } from '../utils/pendingRefresh';

function normalize_requirements(r: { requirement: { id: number; name: string }; value: string; files: [] }) {
    return {
        key: r.requirement.id,
        label: r.requirement.name,
        value: r.value,
        files: r.files,
    };
}

export interface IPartListResponse {
    count: number;
    next: null;
    previous: null;
    results: IPartList[];
}

export interface ICreatedBy {
    id: number;
    first_name: string;
    last_name: string;
    fullname: string;
    email: string;
    is_active: boolean;
    biography: null;
    picture: null;
}

export interface IPartList {
    id: number;
    created_by: ICreatedBy;
    created_time: string;
    quote: any;
    master_product: any;
    material: any;
    finish: any;
    coating: any;
    quantity: number;
    unit_price: string;
    total_price: string;
}
interface ErrorField {
    fieldName: string;
    lineItem: string;
}

/**
 * The variation-defining attributes of a line item change, the ones every quantity of a
 * multiple-quantities part shares. Spreading the full payload would copy per-row fields
 * (price_id, quote_action, delivery, etc.) onto every sibling and overwrite their own state.
 */
const SHARED_VARIATION_KEYS = [
    'product',
    'manufacturing_type',
    'quantity_frequency',
    'batch_shipment',
    'needs_more_quantities',
    'quantities',
    'manual_adjustment',
];

const sharedVariationPayload = (payload: { [key: string]: any }) =>
    SHARED_VARIATION_KEYS.reduce<{ [key: string]: any }>((acc, key) => {
        if (key in payload) {
            acc[key] = payload[key];
        }
        return acc;
    }, {});

class QuoteStore {
    signed_uid?: string | null = null;
    loading = false;
    IsUpdaItemLoading = false;
    /** Line items with a save in flight, so each part shows its own spinner. */
    private savingLineItemIds = observable.map<number, true>();
    // True while a multiple-quantities fan-out (add/remove) is reconciling, so the
    // prices table can show a spinner instead of silently mutating rows.
    isSyncingQuantities = false;
    loading_accounts = false;

    quote?: QuoteProps | null = null;
    is_new_contact = false;

    // All information of contact related to the current quote
    contact: ContactDetailProps | null = null;

    partTolerances: Array<HoleTorelances> = [];
    holeTolerances: Array<PartTolerances> = [];
    // Plain flag (not observed): only stops two refreshes from running at once.
    refreshingAfterReconnect = false;
    // Counts the writes started, so a refresh whose answer arrived after a write is discarded
    // (that answer may predate the write). Plain: nothing renders from it.
    private writeSeq = 0;
    // uids being deleted: a refresh or socket payload must not bring them back meanwhile.
    private pendingDeletes = new Set<string>();
    // The server answered 404 for a quote the form had loaded: keep the form, stop asking.
    quoteUnavailable = false;
    finishes: Array<FinishesProps> = [];
    coatings: Array<CoatingsProps> = [];
    // Every part with the same material needs the same list; fetch it once.
    private coatingsByMaterial = createRequestCache((materialId: number) =>
        requests
            .get(`/customer/coatings/by_material/?material_id=${materialId}`)
            .then((response): Array<CoatingsProps> => response.results || [])
    );
    materials: Array<MaterialProps> = [];
    accounts: Array<AccountProps> = [];

    selectedLineItems = [];
    collapsedLineItems: Array<number> = [];

    default_requirements: {
        key: number;
        label: string;
        value: string | null;
        files: number[];
    }[] = [];

    users: Array<UserProps> = [];

    token: string | null = '';
    for_staff = false;

    current_user?: UserProps = {} as any;

    isConnectedToSocket?: boolean = false;

    previous_quoted_parts: any[] = [];
    loading_quote_items = false;
    pendingEmbeddedExtractionQueue: PendingEmbeddedExtraction[] = [];
    extractEmbeddedLoading = false;

    additional_requirements: IRequirement[] = [];
    default_documents: ContactDocumentsProps[] = [];
    acquisition_source: [] = [];
    acquisition_source_contact: [] = [];

    // Temporary data for debugging safe auto quotes
    user_is_staff = false;
    user_is_tester = false;

    entities: IPartList[] = [];

    part_list_response: IPartListResponse | null = null;

    /** Serialises multi-quantity reconciliations so rapid tag additions don't race. */
    quantitySyncChain: Promise<unknown> = Promise.resolve();

    /** Count of in-flight quantity syncs; drives `isSyncingQuantities`. */
    quantitySyncPending = 0;

    constructor() {
        makeObservable(this, {
            // Observables
            signed_uid: observable,
            loading: observable,
            IsUpdaItemLoading: observable,
            isSyncingQuantities: observable,
            loading_accounts: observable,
            quote: observable,
            is_new_contact: observable,
            contact: observable,
            partTolerances: observable,
            holeTolerances: observable,
            finishes: observable,
            coatings: observable.ref,
            materials: observable,
            accounts: observable,
            selectedLineItems: observable,
            collapsedLineItems: observable,
            default_requirements: observable,
            users: observable,
            token: observable,
            for_staff: observable,
            current_user: observable,
            user_is_staff: observable,
            isConnectedToSocket: observable,
            quoteUnavailable: observable,
            previous_quoted_parts: observable,
            loading_quote_items: observable,
            pendingEmbeddedExtractionQueue: observable,
            extractEmbeddedLoading: observable,
            additional_requirements: observable,
            default_documents: observable,
            acquisition_source: observable,
            acquisition_source_contact: observable,

            // Actions
            setQuote: action,
            setLineItem: action,
            updateConversionProgress: action,
            setCollapsed: action,
            collapseAllLineItems: action,
            expandAllLineItems: action,
            expandLineItemsWithErrors: action,
            setConnectedSocket: action,
            setSignedUID: action,
            setToken: action,
            setLoading: action,
            newQuote: action,
            getQuote: action,
            updateQuote: action,
            getRequote: action,
            newLineItemWithFiles: action,
            duplicateItem: action,
            syncQuantities: action,
            reconcileQuantities: action,
            updateVariationGroup: action,
            collapseVariationGroup: action,
            updateLineItem: action,
            extractEmbedded3d: action,
            queueEmbeddedExtractionFromUpload: action,
            maybeQueueEmbeddedExtraction: action,
            enqueueEmbeddedExtraction: action,
            clearPendingEmbeddedExtraction: action,
            confirmExtractEmbedded3d: action,
            updateMultipleLineItems: action,
            updateMultipleSpecs: action,
            updateMultipleQuantities: action,
            refreshQuote: action,
            getBulkPrices: action,
            deleteLineItem: action,
            deleteLineItems: action,
            deleteMasterProduct: action,
            deleteMasterProducts: action,
            deleteAll: action,
            newLineItem: action,
            exportToPdf: action,
            sendToReview: action,
            getTolerances: action,
            getFinishes: action,
            getCoatings: action,
            getCoatingsByMaterial: action,
            getMaterials: action,
            getAdditionalRequirements: action,
            getAccounts: action,
            getUsers: action,
            lineItems: action,
            safeLineItems: action,
            setProduct: action,
            hasErrorFiles: action,
            needsRFQ: action,
            needs3DFiles: action,
            needs2DFiles: action,
            setForStaff: action,
            uploadAdditionalFile: action,
            setIsNewContact: action,
            setCurrentUser: action,
            updateContactInfo: action.bound,
            getContactDetails: action,
            getAdditionalRequirementsContactAccount: action,
            updateRequiredQuantities: action,
            getQuoteLineItems: action,
            createLineItemFromExistingLineItems: action,
            searchPreviousParts: action,
            clean_previous_items: action,
            update_quote_field: action,
            update_quote_fields: action,
            setAcquisitionSource: action,

            // Computed
            pendingEmbeddedExtraction: computed,
            pendingEmbeddedExtractionQueueSize: computed,
            quote_uid: computed,
            needsToUsePolling: computed,
            hasPendingWork: computed,
            canBeOrdered: computed,
            errorFields: computed,
            isRequote: computed,
            isNewContact: computed,
            getFirstName: computed,
            getLastName: computed,
            getEmail: computed,
            getCompany: computed,
            getPhone: computed,
            getZip: computed,
            getTitle: computed,
            getRelatedContact: computed,
            getAdditionalFiles: computed,
            isAllFileAnalyzingCompleted: computed,
            hasBuyNow: computed,
            hasManualQuote: computed,
        });
    }

    get pendingEmbeddedExtraction(): PendingEmbeddedExtraction | null {
        return this.pendingEmbeddedExtractionQueue[0] ?? null;
    }

    get pendingEmbeddedExtractionQueueSize(): number {
        return this.pendingEmbeddedExtractionQueue.length;
    }

    setQuote(data: QuoteProps | undefined | null) {
        this.quote = data;
        this.loading = false;
        if (data) this.quoteUnavailable = false;
    }

    /** Called at the start of every request that changes the quote on the server. */
    private noteWrite() {
        this.writeSeq += 1;
    }

    /** A 404 for a quote already on screen: keep it, say so, and stop asking for updates. */
    private markQuoteUnavailable() {
        runInAction(() => {
            this.quoteUnavailable = true;
        });
    }

    setLineItem(data: QuoteLineItemProps) {
        if (data?.uid && this.pendingDeletes.has(data.uid)) return;
        runInAction(() => {
            if (this.quote) {
                const exists = this.quote.line_items.some(item => item.id == data.id);

                const preserveDrawingSuggestions = (
                    incoming: QuoteLineItemProps,
                    existing?: QuoteLineItemProps
                ): QuoteLineItemProps => {
                    const incomingAttachments = incoming.product?.attachments;
                    const incomingHasFiles2d =
                        !!incomingAttachments && Object.prototype.hasOwnProperty.call(incomingAttachments, 'files_2d');
                    const incomingFiles2d = incomingHasFiles2d ? incomingAttachments?.files_2d ?? null : undefined;
                    const existingFiles2d = existing?.product?.attachments?.files_2d;
                    const incomingSuggestions = incomingFiles2d?.suggestions ?? [];
                    const existingSuggestions = existingFiles2d?.suggestions ?? [];
                    const hasIncomingField = (field: string) =>
                        incomingFiles2d != null && Object.prototype.hasOwnProperty.call(incomingFiles2d, field);

                    if (incomingHasFiles2d && incomingFiles2d == null) {
                        return incoming;
                    }

                    const sameProduct =
                        incoming.product != null &&
                        existing?.product != null &&
                        ((incoming.product.id != null && existing.product.id === incoming.product.id) ||
                            (incoming.product.master_product != null &&
                                existing.product.master_product === incoming.product.master_product));

                    if (existingFiles2d && incoming.product && sameProduct) {
                        return {
                            ...incoming,
                            product: {
                                ...incoming.product,
                                attachments: {
                                    ...(existing?.product?.attachments ?? {}),
                                    ...(incoming.product.attachments ?? {}),
                                    files_2d: {
                                        ...existingFiles2d,
                                        ...incomingFiles2d,
                                        suggestions: hasIncomingField('suggestions')
                                            ? incomingSuggestions
                                            : existingSuggestions,
                                        suggested_materials_text: hasIncomingField('suggested_materials_text')
                                            ? incomingFiles2d?.suggested_materials_text ?? null
                                            : existingFiles2d.suggested_materials_text ?? null,
                                        suggested_finishes_text: hasIncomingField('suggested_finishes_text')
                                            ? incomingFiles2d?.suggested_finishes_text ?? null
                                            : existingFiles2d.suggested_finishes_text ?? null,
                                        suggested_coatings_text: hasIncomingField('suggested_coatings_text')
                                            ? incomingFiles2d?.suggested_coatings_text ?? null
                                            : existingFiles2d.suggested_coatings_text ?? null,
                                    },
                                },
                            },
                        };
                    }

                    return incoming;
                };

                if (exists) {
                    // Replace only the changed item; reassigning the whole array made every
                    // observer of line_items treat all parts as changed.
                    const index = this.quote.line_items.findIndex((item: QuoteLineItemProps) => item.id == data.id);
                    this.quote.line_items[index] = preserveDrawingSuggestions(data, this.quote.line_items[index]);
                } else {
                    // A realtime update can arrive before the upload POST response
                    // adds the line item to the list. Upsert it so the analysis
                    // status (e.g. completed=true) is not lost in that race.
                    this.quote.line_items = [...this.quote.line_items, data];
                }
            }
        });
    }

    updateConversionProgress(lineItemPk: number, progress: { msg?: string; stage?: string; current?: number }) {
        runInAction(() => {
            if (!this.quote) return;

            // Replace only the item that changed (see setLineItem).
            const index = this.quote.line_items.findIndex(
                (item: QuoteLineItemProps) => item.id == lineItemPk && item.product
            );
            if (index === -1) return;

            const item = this.quote.line_items[index];
            this.quote.line_items[index] = {
                ...item,
                product: {
                    ...item.product,
                    analysis_status: {
                        ...item.product.analysis_status,
                        file_3d: {
                            completed: false,
                            message: { msg: progress.msg || '', current: progress.current || 0 } as {
                                msg: string;
                                current: number;
                            },
                            status: 'PENDING' as const,
                        },
                    },
                },
            };
        });
    }

    setProduct(data: ProductProps) {
        runInAction(() => {
            if (this.quote) {
                this.quote.line_items = this.quote.line_items.map(item => {
                    if (item.product.id == data.id) {
                        item = {
                            ...item,
                            product: data,
                        };
                    }
                    return item;
                });
            }
        });
    }

    setCollapsed(id: string | number) {
        runInAction(() => {
            if (this.collapsedLineItems.includes(Number(id))) {
                this.collapsedLineItems = this.collapsedLineItems.filter(x => Number(x) != Number(id));
            } else {
                this.collapsedLineItems.push(Number(id));
            }
        });
    }

    collapseAllLineItems() {
        this.collapsedLineItems = this?.quote?.line_items?.map(x => Number(x.id)) || [];
    }

    expandAllLineItems() {
        this.collapsedLineItems = [];
    }

    /**
     * Expand every master-product group that has a field error.
     * Collapse state is keyed by the first line item id of each group
     * (`MasterProduct` / Collapse All), so that id is what we remove.
     */
    expandLineItemsWithErrors() {
        const errorIds = new Set(this.errorFields.map(field => Number(field.lineItem)));
        if (errorIds.size === 0 || !this.quote?.line_items?.length) {
            return;
        }

        const collapseKeyByMaster = new Map<number, number>();
        const mastersWithErrors = new Set<number>();

        for (const item of this.quote.line_items) {
            const masterId = Number(item.product.master_product);
            if (!collapseKeyByMaster.has(masterId)) {
                collapseKeyByMaster.set(masterId, Number(item.id));
            }
            if (errorIds.has(Number(item.id))) {
                mastersWithErrors.add(masterId);
            }
        }

        const collapseKeysToExpand = new Set<number>();
        mastersWithErrors.forEach(masterId => {
            const collapseKey = collapseKeyByMaster.get(masterId);
            if (collapseKey != null) {
                collapseKeysToExpand.add(collapseKey);
            }
        });

        this.collapsedLineItems = this.collapsedLineItems.filter(id => !collapseKeysToExpand.has(Number(id)));
    }

    setConnectedSocket(connected: boolean) {
        this.isConnectedToSocket = connected;
    }

    setSignedUID(signed_id: string) {
        this.signed_uid = signed_id;
    }

    async fetchAndSetAcquisitionSource(source: string) {
        try {
            const response = await requests.get(`/staff/acquisition-source/?source=${source}`);
            const valuesArray = response.results.map((item: { value: string }) => item.value);
            runInAction(() => {
                if (source === 'LC') {
                    this.acquisition_source_contact = valuesArray;
                } else {
                    this.acquisition_source = valuesArray;
                }
            });
        } catch (error) {
            console.error('Error fetching acquisition source:', error);
        }
    }

    setAcquisitionSource(quote_with: 'NC' | 'EC' | 'LC') {
        if (quote_with === 'NC') {
            this.fetchAndSetAcquisitionSource(quote_with);
            this.fetchAndSetAcquisitionSource('LC');
        } else {
            // Default Value EC
            this.fetchAndSetAcquisitionSource('EC');
        }
    }

    get quote_uid() {
        if (!this.token && this.signed_uid) {
            return this.signed_uid;
        }
        return this.quote?.uid;
    }

    /**
     * If is not authenticated (no token) and has pending files to analyze use polling because web sockets is only for
     * authenticated users
     * @returns {boolean}
     */
    get needsToUsePolling() {
        if (!this.token && this.signed_uid) {
            return true;
        } else {
            return false;
        }
    }

    /** An analysis that has not ended or a price being calculated: the form keeps asking for updates. */
    get hasPendingWork() {
        if (this.quoteUnavailable) return false;
        return hasPendingWork(this.quote?.line_items ?? []);
    }

    /**
     * Items safe to quote can be ordered
     * */
    get canBeOrdered() {
        return (
            this.quote &&
            this.quote.line_items.length > 0 &&
            this.quote.line_items.filter(item => item.needs_manual_quote).length === 0
        );
    }

    setToken(token: string) {
        runInAction(() => {
            this.token = token;
        });
    }

    setLoading(loading = false) {
        runInAction(() => {
            this.loading = loading;
        });
    }

    newQuote(payload = {}) {
        return requests
            .post(`/customer/quote/new/`, payload)
            .then(data => {
                this.setQuote(data);
            })
            .catch(err => {
                console.log(err);
                message.error('Error, please try again');
            });
    }

    get isAllFileAnalyzingCompleted() {
        // Return false if there are no line items
        if (!this.quote?.line_items || this.quote?.line_items?.length === 0) {
            return false;
        }

        const isAllCompleted = this.quote.line_items.every(
            item =>
                item.product?.analysis_status?.file_2d?.completed && item.product?.analysis_status?.file_3d?.completed
        );

        // Return true if all files have completed analysis
        return isAllCompleted;
    }

    getQuote() {
        if (this.quote_uid) {
            this.loading = true;
            // The answer belongs to this quote: if another one is open by then, it is dropped.
            const uid = this.quote_uid;

            return requests
                .get(`/customer/quote/${uid}/`)
                .then(data => {
                    if (this.quote_uid !== uid) return;
                    this.setQuote(data);
                })
                .catch(error => {
                    if (error?.response?.status === 404 && this.quote_uid === uid) {
                        this.markQuoteUnavailable();
                    }
                })
                .finally(() => {
                    runInAction(() => {
                        this.loading = false;
                    });
                });
        } else {
            this.loading = false;
            return Promise.resolve();
        }
    }

    /**
     * Brings the quote up to date after the realtime socket reconnected (updates sent while it
     * was down are not replayed). It replaces the line items one by one, without a loading
     * state, and leaves alone the ones being saved so an edit in flight is not undone.
     */
    async refreshQuote(): Promise<void> {
        if (!this.quote_uid || this.refreshingAfterReconnect) return;
        this.refreshingAfterReconnect = true;
        const writesBefore = this.writeSeq;
        // The answer belongs to this quote: another quote opened meanwhile must not take it.
        const uid = this.quote_uid;
        try {
            const data = await requests.get(`/customer/quote/${uid}/`);
            if (this.quote_uid !== uid) return;
            // A write started while the answer was travelling: it may predate that write and
            // would undo it. The next refresh or socket event brings the current state.
            if (this.writeSeq !== writesBefore || !this.quote) return;
            const incoming: QuoteLineItemProps[] = data?.line_items ?? [];
            const incomingIds = new Set(incoming.map(item => item.id));
            runInAction(() => {
                this.quoteUnavailable = false;
                // Quote-level fields (all_items_safe_auto_quote drives the checkout buttons).
                const { line_items: _lines, ...quoteFields } = data ?? {};
                if (this.quote) Object.assign(this.quote, quoteFields);
                incoming.forEach(item => {
                    if (this.isLineItemSaving(item.id) || this.pendingDeletes.has(item.uid)) return;
                    const current = this.quote?.line_items.find(existing => existing.id == item.id);
                    // Untouched parts keep their identity so they do not re-render.
                    if (current && JSON.stringify(toJS(current)) === JSON.stringify(item)) return;
                    this.setLineItem(item);
                });
                if (this.quote) {
                    // Lines removed elsewhere (another tab, a bulk replace) have no deletion event.
                    this.quote.line_items = this.quote.line_items.filter(
                        existing => incomingIds.has(existing.id) || this.isLineItemSaving(existing.id)
                    );
                }
            });
        } catch (error: any) {
            if (this.quote_uid !== uid) return;
            if (error?.response?.status === 404) {
                this.markQuoteUnavailable();
            } else {
                console.error('Refreshing the quote failed:', error);
            }
        } finally {
            this.refreshingAfterReconnect = false;
        }
    }

    updateQuote(payload = {}) {
        return requests
            .patch(`/customer/quote/${this.quote_uid}/`, payload)
            .then(() => {
                this.getQuote();
            })
            .catch(err => {
                console.log(err);
                message.error('Error, please try again');
            });
    }

    getRequote(id: number) {
        return requests.post(`/staff/quotes/${id}/requote/`);
    }

    newLineItemWithFiles(payload = {}, onUploadProgress: any, quoteUid?: string) {
        this.noteWrite();
        const targetUid = quoteUid ?? this.quote_uid;
        const token = this.token ? getAuthToken() : null;
        const authorization = token ? { authorization: `Token ${token}` } : {};

        const options = {
            headers: {
                'Content-Type': 'multipart/form-data',
                ...authorization,
            },

            onUploadProgress: onUploadProgress && onUploadProgress,
        };

        return requests
            .post(`/customer/quote/${targetUid}/new-item/`, payload, options)
            .then(data => {
                // A part queued for a quote that is no longer open is saved there and shows up
                // when that quote is opened again; the one on screen must not take it.
                if (targetUid !== this.quote_uid) return data;
                runInAction(() => {
                    if (this.quote) {
                        // A realtime update may have already upserted this line item
                        // (its analysis can finish before this POST resolves). Avoid
                        // pushing a stale duplicate that would revert completed=true.
                        const exists = this.quote.line_items.some(item => item.id == data.id);
                        if (!exists) {
                            this.quote.line_items.push(data);
                        }
                    }
                });
                this.maybeQueueEmbeddedExtraction(data);
                return data;
            })
            .catch(err => {
                console.log(err);
                message.error('Error, please try again');
            });
    }

    duplicateItem(master_product: number, source_item?: number) {
        return requests
            .post(
                `/customer/quote/${this.quote_uid}/duplicate-item/${master_product}/`,
                source_item ? { source_item } : undefined
            )
            .then(data => {
                runInAction(() => {
                    if (this.quote) {
                        this.quote.line_items.push(data);
                    }
                    this.getQuote();
                });
            })
            .catch(err => {
                console.log(err);
                message.error('Error, please try again');
            });
    }

    async duplicateMasterProduct(master_product_id: number, duplicated_reason: string): Promise<any> {
        const response = await post(`/customer/quote/${this.quote_uid}/duplicate-master-product/`, {
            master_product: master_product_id,
            duplicated_reason: duplicated_reason,
        });

        // Unlock UI immediately from the duplicate response so Confirm does not
        // look like a no-op while getQuote is in flight (PTB-6837 regression:
        // without the Analyzing spinner there was no other feedback).
        runInAction(() => {
            if (!this.quote?.line_items?.length || !response?.id) {
                return;
            }
            this.quote.line_items = this.quote.line_items.map(item => {
                const currentMasterId = item.master_product_details?.id ?? item.product?.master_product;
                if (currentMasterId !== master_product_id) {
                    return item;
                }
                return {
                    ...item,
                    // Keep list order stable across the master-product id swap (PTB-6840).
                    position: item.position,
                    product: {
                        ...item.product,
                        master_product: response.id,
                    },
                    master_product_details: {
                        ...item.master_product_details,
                        ...response,
                    },
                };
            });
        });

        await this.getQuote();
        return response;
    }

    /**
     * Auto fan-out entry point. Called whenever a quantity is added to the
     * multi-quantity input. Reconciliations are serialised through a promise
     * chain so tags added in quick succession never race each other.
     */
    syncQuantities(baseItem: QuoteLineItemProps, quantities: Array<number | string>) {
        // Show the spinner immediately (before the debounced network work) and
        // clear it only once every queued sync has settled — a pending counter
        // keeps rapid edits from flickering the spinner off between operations.
        runInAction(() => {
            this.quantitySyncPending += 1;
            this.isSyncingQuantities = true;
        });

        // Snapshot the requested values now: the queued reconciliation runs after
        // later calls may have already mutated/replaced the caller's array.
        const requestedQuantities = [...quantities];
        // A prior queued reconciliation may repurpose/delete this exact id (e.g.
        // when its quantity gets dropped); fall back to the group so this edit
        // still lands on the surviving sibling instead of silently no-oping.
        const requestedGroupId = baseItem.quantity_group ?? null;

        this.quantitySyncChain = this.quantitySyncChain
            .catch(() => undefined)
            .then(() =>
                this.reconcileQuantities(
                    baseItem.id,
                    baseItem.product.master_product,
                    requestedQuantities,
                    requestedGroupId
                )
            )
            .finally(() => {
                runInAction(() => {
                    this.quantitySyncPending = Math.max(0, this.quantitySyncPending - 1);
                    this.isSyncingQuantities = this.quantitySyncPending > 0;
                });
            });
        return this.quantitySyncChain;
    }

    /**
     * Reconciles this variation's line items to exactly match the desired
     * quantities (full sync, both add and remove; idempotent). Missing quantities
     * are created via `duplicate-item` copying the base variation (so
     * material/coating/finish are preserved); dropped quantities have their line
     * items deleted. The base is repurposed for the first added quantity when its
     * own quantity is being dropped (avoids delete + create churn). Every member
     * shares a `quantity_group` (so a master product can hold several distinct
     * variations, each with its own quantities table) and keeps `quantities=[]`
     * so the backend safe-auto-quote guards never fire and each quantity is
     * priced independently.
     */
    async reconcileQuantities(
        baseItemId: number,
        masterProduct: number,
        quantities: Array<number | string>,
        quantityGroup?: string | null
    ) {
        this.noteWrite();
        // Sanitise: integers >= 1, de-duplicated, preserving input order.
        const desired = Array.from(
            new Set(quantities.map(q => Math.trunc(Number(q))).filter(q => Number.isFinite(q) && q >= 1))
        );

        if (desired.length === 0) {
            return;
        }

        const lineItems = this.quote?.line_items || [];
        // Fall back to any surviving member of the same quantity_group when the
        // original id was already repurposed/removed by an earlier queued sync.
        const baseItem =
            lineItems.find(li => li.id === baseItemId) ??
            (quantityGroup ? lineItems.find(li => li.quantity_group === quantityGroup) : undefined);
        if (!baseItem) {
            return;
        }

        // A variation group is identified by quantity_group; reuse the base's when
        // it already has one (toggled/refreshed), otherwise mint a fresh one.
        const groupId = baseItem.quantity_group || uuidv4();

        // Current members of THIS variation group (other variations of the same
        // master product must not be touched). The base is always a member.
        const rawGroup = (this.quote?.line_items || []).filter(
            li => li.id === baseItem.id || (li.quantity_group && li.quantity_group === groupId)
        );

        // Prune stale duplicate siblings that share the same quantity — otherwise
        // toRemove only checks quantity membership and both rows survive forever,
        // leaving hidden duplicates that inflate totals.
        const latestItemByQuantity = new Map<number, QuoteLineItemProps>();
        const duplicateRowsToRemove: QuoteLineItemProps[] = [];
        [...rawGroup]
            .sort((a, b) => b.id - a.id)
            .forEach(li => {
                if (!latestItemByQuantity.has(li.quantity)) {
                    latestItemByQuantity.set(li.quantity, li);
                } else {
                    duplicateRowsToRemove.push(li);
                }
            });

        const currentGroup = [...latestItemByQuantity.values()];
        const existing = new Set(currentGroup.map(li => li.quantity));

        const toAdd = desired.filter(q => !existing.has(q));
        const toRemove = [...duplicateRowsToRemove, ...currentGroup.filter(li => !desired.includes(li.quantity))];

        this.loading = true;

        try {
            // Reducing to a single quantity must collapse the group entirely
            // (clear quantity_group / needs_more_quantities on the survivor),
            // otherwise it keeps behaving like a multi-quantity variant with
            // just one member.
            if (desired.length === 1) {
                const survivor =
                    currentGroup.find(li => li.quantity === desired[0]) ??
                    [...currentGroup].sort((a, b) => a.id - b.id)[0];

                // Use rawGroup so stale duplicate siblings are removed too.
                for (const li of rawGroup) {
                    if (li.id !== survivor.id) {
                        await requests.delete(`/customer/quote/${this.quote_uid}/remove-item/${li.uid}/`);
                    }
                }

                await this.saveLineItemOrThrow({
                    id: survivor.id,
                    quantity: desired[0],
                    quantities: [],
                    needs_more_quantities: false,
                    quantity_group: null,
                });

                await this.getQuote();
                return;
            }

            // Reuse the base for the first new quantity only when its current
            // quantity is being dropped (repurpose instead of delete + create).
            const baseDropped = !desired.includes(baseItem.quantity);
            let reuseBase = baseDropped && toAdd.length > 0;
            let baseReused = false;

            // Ensure the kept base carries the group id + multi flag.
            if (
                !reuseBase &&
                !baseDropped &&
                (baseItem.quantity_group !== groupId || !baseItem.needs_more_quantities)
            ) {
                await this.saveLineItemOrThrow({
                    id: baseItem.id,
                    quantities: [],
                    needs_more_quantities: true,
                    quantity_group: groupId,
                });
            }

            for (const quantity of toAdd) {
                if (reuseBase) {
                    await this.saveLineItemOrThrow({
                        id: baseItem.id,
                        quantity,
                        quantities: [],
                        needs_more_quantities: true,
                        quantity_group: groupId,
                    });
                    reuseBase = false;
                    baseReused = true;
                } else {
                    const created = await requests.post(
                        `/customer/quote/${this.quote_uid}/duplicate-item/${masterProduct}/`,
                        { source_item: baseItem.id }
                    );
                    await this.saveLineItemOrThrow({
                        id: created.id,
                        quantity,
                        quantities: [],
                        needs_more_quantities: true,
                        quantity_group: groupId,
                    });
                }
            }

            // Remove line items for quantities the user dropped. Skip the base if
            // it was repurposed above (it now holds a wanted quantity).
            for (const li of toRemove) {
                if (li.id === baseItem.id && baseReused) {
                    continue;
                }
                await requests.delete(`/customer/quote/${this.quote_uid}/remove-item/${li.uid}/`);
            }

            await this.getQuote();
        } catch (err) {
            // Partial create/update/delete may have already landed on the
            // server; refetch so the UI matches the surviving state.
            await this.getQuote();
            message.error('Error, please try again');
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Applies a line-item update and propagates the same variation-defining
     * attributes (material, finish, coating, process, tolerances, frequency...)
     * to every sibling in the multiple-quantities group, so a group always shares
     * one variation. Each sibling keeps its own quantity; per-quantity price
     * selection (price_id) is not forced across quantities. The backend reprices
     * every item independently.
     */
    async updateVariationGroup(payload: { [key: string]: any }, groupItems: QuoteLineItemProps[] = []) {
        // updateLineItem already reported the failure; do not spread a change that did not save.
        if (!(await this.updateLineItem(payload))) {
            return;
        }

        const sharedPayload = sharedVariationPayload(payload);

        const siblings = groupItems.filter(li => li.id !== payload.id);
        for (const sibling of siblings) {
            const saved = await this.updateLineItem({
                ...sharedPayload,
                id: sibling.id,
                quantity: sibling.quantity,
            });
            if (!saved) {
                // Some variations already changed: reload so the form shows what the server kept.
                await this.getQuote();
                return;
            }
        }

        await this.getQuote();
    }

    /**
     * Collapses a multiple-quantities group back to a single item: keeps the base
     * (the first/main quantity) and soft-deletes the sibling variations, then
     * flips the base back to single mode. Soft-delete reuses the standard
     * `remove-item` endpoint (sets `deleted=True`).
     */
    async collapseVariationGroup(baseItemId: number, quantityGroup?: string | null) {
        this.noteWrite();
        runInAction(() => {
            this.IsUpdaItemLoading = true;
        });

        try {
            if (quantityGroup) {
                const siblings = (this.quote?.line_items || []).filter(
                    li => li.quantity_group === quantityGroup && li.id !== baseItemId
                );
                for (const sibling of siblings) {
                    await requests.delete(`/customer/quote/${this.quote_uid}/remove-item/${sibling.uid}/`);
                }
            }

            await this.saveLineItemOrThrow({
                id: baseItemId,
                needs_more_quantities: false,
                quantities: [],
                quantity_group: null,
            });

            await this.getQuote();
        } catch (err) {
            message.error('Error, please try again');
        } finally {
            runInAction(() => {
                this.IsUpdaItemLoading = false;
            });
        }
    }

    isLineItemSaving(lineItemId?: number) {
        return lineItemId != null && this.savingLineItemIds.has(lineItemId);
    }

    /**
     * Saves a 3D/2D file change. Files belong to the master product, so when other variants
     * of the part are in this quote the quote is reloaded: the server reprices them, and they
     * would otherwise keep showing the old file, price and analysis state.
     */
    async updateLineItemFiles(payload: { id: number; product: { [key: string]: any } }) {
        const masterProductId = this.quote?.line_items?.find(li => li.id === payload.id)?.product?.master_product;
        const saved = await this.updateLineItem(payload);
        const hasSiblings = (this.quote?.line_items ?? []).some(
            li => li.id !== payload.id && masterProductId != null && li.product?.master_product === masterProductId
        );
        if (saved && hasSiblings) {
            await this.getQuote();
        }
        return saved;
    }

    /** Like updateLineItem, but throws when the save failed so multi-step operations stop. */
    private async saveLineItemOrThrow(payload: { id?: number; [key: string]: any }) {
        const saved = await this.updateLineItem(payload);
        if (!saved) {
            throw new Error('Line item update failed');
        }
        return saved;
    }

    /**
     * Saves a line item change to the quote it belongs to. A line item card flushes its pending
     * save when it unmounts, and when the user opened another quote the store already holds
     * that one: the change then goes to the card's quote, with its other quantities, and the
     * open quote is neither changed nor reloaded.
     */
    async saveLineItemToQuote(
        quoteUid: string | undefined,
        payload: { id?: number; [key: string]: any },
        groupItems: QuoteLineItemProps[] = []
    ) {
        if (!quoteUid || quoteUid === this.quote_uid) {
            return groupItems.length > 1
                ? this.updateVariationGroup(payload, groupItems)
                : this.updateLineItem(payload);
        }
        try {
            await this.updateLineItemOfQuote(quoteUid, payload);
            const sharedPayload = sharedVariationPayload(payload);
            for (const sibling of groupItems.filter(li => li.id !== payload.id)) {
                await this.updateLineItemOfQuote(quoteUid, {
                    ...sharedPayload,
                    id: sibling.id,
                    quantity: sibling.quantity,
                });
            }
        } catch (err) {
            message.error('A change to a part of the previous quote could not be saved.');
        }
    }

    /** Update a line item of a quote that is not the open one, without touching the store. */
    updateLineItemOfQuote(quoteUid: string, payload: { id?: number; [key: string]: any }) {
        return requests.patch(`/customer/quote/${quoteUid}/update-item/`, payload);
    }

    async updateLineItem(payload: { id?: number; [key: string]: any } = {}) {
        this.noteWrite();
        const lineItemId = payload.id;
        this.loading = true;
        this.IsUpdaItemLoading = true;
        if (lineItemId != null) this.savingLineItemIds.set(lineItemId, true);

        return await requests
            .patch(`/customer/quote/${this.quote_uid}/update-item/`, payload)
            .then(data => {
                runInAction(() => {
                    if (this.quote) {
                        // Replace only the saved item (see setLineItem) so the other parts keep
                        // their props and do not re-render.
                        const index = this.quote.line_items.findIndex(item => item.uid === data.uid);
                        if (index !== -1) {
                            this.quote.line_items[index] = data;
                        }
                    }

                    this.maybeQueueEmbeddedExtraction(data);
                });
                return data;
            })
            .catch(err => {
                message.error('Error, please try again');
            })
            .finally(() => {
                this.setLoading(false);
                runInAction(() => {
                    this.IsUpdaItemLoading = false;
                    if (lineItemId != null) this.savingLineItemIds.delete(lineItemId);
                });
            });
    }

    maybeQueueEmbeddedExtraction(lineItem: QuoteLineItemProps, partLabel?: string) {
        const payload = lineItem.embedded_pdf_attachments;
        if (!payload?.attachments?.length) {
            return;
        }

        const pending = buildPendingEmbeddedExtraction(
            lineItem.id,
            payload.drawing_file_id,
            payload.attachments,
            partLabel || lineItem.name
        );

        if (pending) {
            this.enqueueEmbeddedExtraction(pending);
        }
    }

    enqueueEmbeddedExtraction(item: PendingEmbeddedExtraction) {
        this.pendingEmbeddedExtractionQueue = enqueueEmbeddedExtraction(this.pendingEmbeddedExtractionQueue, item);
    }

    queueEmbeddedExtractionFromUpload(
        lineItemId: number,
        drawingFileId: number,
        attachments: PendingEmbeddedExtraction['attachments'],
        pdfFilename?: string
    ) {
        // Most drawings carry no embedded 3D model: that is the normal case, not worth a notice.
        if (!attachments.length) {
            return;
        }

        if (!hasExtractableEmbedded3d(attachments)) {
            message.info('Embedded attachments were found, but none are supported 3D file types.');
            return;
        }

        const pending = buildPendingEmbeddedExtraction(lineItemId, drawingFileId, attachments, pdfFilename);

        if (pending) {
            this.enqueueEmbeddedExtraction(pending);
        }
    }

    clearPendingEmbeddedExtraction() {
        this.pendingEmbeddedExtractionQueue = dequeueEmbeddedExtraction(this.pendingEmbeddedExtractionQueue);
    }

    async confirmExtractEmbedded3d(filenames: string[]) {
        const pending = this.pendingEmbeddedExtraction;
        if (!pending) {
            return;
        }

        this.extractEmbeddedLoading = true;

        try {
            await this.extractEmbedded3d({
                line_item_id: pending.lineItemId,
                drawing_file_id: pending.drawingFileId,
                filenames,
            });
            this.clearPendingEmbeddedExtraction();
        } finally {
            this.extractEmbeddedLoading = false;
        }
    }

    async extractEmbedded3d(payload: { line_item_id: number; drawing_file_id: number; filenames: string[] }) {
        this.loading = true;

        return await requests
            .post(`/customer/quote/${this.quote_uid}/extract-embedded-3d/`, payload)
            .then(data => {
                runInAction(() => {
                    if (this.quote) {
                        this.quote.line_items = this.quote.line_items.map(item => {
                            if (item.uid === data.uid) {
                                return data;
                            }
                            return item;
                        });
                    }

                    this.maybeQueueEmbeddedExtraction(data);
                });
                return data;
            })
            .catch(err => {
                message.error('Could not extract embedded 3D files');
                throw err;
            })
            .finally(() => {
                this.setLoading(false);
            });
    }

    async updateMultipleLineItems(payload = {}) {
        this.loading = true;

        return await requests
            .patch(`/customer/quote/${this.quote_uid}/update-multiple-items/`, payload)
            .then(data => {
                this.setQuote(data);
            })
            // Rejects on failure: the callers show the server's error details.
            .finally(() => {
                this.setLoading(false);
            });
    }

    /**
     * Changes material, finish and/or coating of several line items (omitted = keep).
     * Resolves with the per-line results; the updated line items replace their copies in the
     * quote one by one, so the other parts do not re-render. A preview (`dryRun`) changes nothing.
     */
    async updateMultipleSpecs(lineItemIds: number[], specs: BulkSpecs, dryRun = false): Promise<BulkResult[]> {
        // The parts show their saving state while the server reprices them.
        if (!dryRun) {
            this.noteWrite();
            runInAction(() => lineItemIds.forEach(id => this.savingLineItemIds.set(id, true)));
        }
        try {
            const data = await requests.patch(
                `/customer/quote/${this.quote_uid}/update-multiple-specs/`,
                buildSpecsPayload(lineItemIds, specs, dryRun)
            );
            const results: BulkResult[] = data?.results ?? [];
            if (!dryRun) {
                results.forEach(result => {
                    if (result.line_item) this.setLineItem((result.line_item as unknown) as QuoteLineItemProps);
                });
            }
            return results;
        } finally {
            if (!dryRun) {
                runInAction(() => lineItemIds.forEach(id => this.savingLineItemIds.delete(id)));
            }
        }
    }

    /**
     * Adds or replaces the quantities of the variations of several line items. The server
     * answers per variation with the lines it created and removed: they are put in or taken
     * out of the quote, and the surviving lines of the variation are refreshed. A preview
     * (`dryRun`) changes nothing.
     */
    async updateMultipleQuantities(
        lineItemIds: number[],
        quantities: number[],
        mode: QuantityMode,
        dryRun = false
    ): Promise<QuantityResult[]> {
        if (!dryRun) {
            this.noteWrite();
            runInAction(() => lineItemIds.forEach(id => this.savingLineItemIds.set(id, true)));
        }
        try {
            const data = await requests.patch(
                `/customer/quote/${this.quote_uid}/update-multiple-quantities/`,
                buildQuantitiesPayload(lineItemIds, quantities, mode, dryRun)
            );
            const results: QuantityResult[] = data?.results ?? [];
            if (!dryRun) {
                const removed = new Set(results.flatMap(result => result.removed ?? []));
                // One transaction: a Replace deletes the old lines and creates the new ones. Observers
                // must not see the quote between the two with no lines, or the form drops its whole
                // items section (and the bulk panel with the result it is about to show).
                runInAction(() => {
                    if (this.quote && removed.size > 0) {
                        this.quote.line_items = this.quote.line_items.filter(item => !removed.has(item.id));
                    }
                    results.forEach(result =>
                        result.line_items?.forEach(item => this.setLineItem((item as unknown) as QuoteLineItemProps))
                    );
                });
            }
            return results;
        } finally {
            if (!dryRun) {
                runInAction(() => lineItemIds.forEach(id => this.savingLineItemIds.delete(id)));
            }
        }
    }

    /** Staff only: the internal reasons why a line item is not priced automatically. */
    getPricingFeedback(
        line_item: number
    ): Promise<{ line_item_id: number; auto_quoted: boolean; reasons: Array<string> }> {
        return requests.get(`/customer/quote/${this.quote_uid}/pricing-feedback/`, {
            params: { line_item_id: line_item },
        });
    }

    getBulkPrices(line_item: number, quantities: Array<number>) {
        const options = {
            params: {
                quantities: quantities.join(','),
            },
        };
        return requests.get(`/customer/quote/${this.quote_uid}/bulk-prices/${line_item}/`, options);
    }

    deleteLineItem(item_uid: string) {
        this.noteWrite();
        this.pendingDeletes.add(item_uid);
        this.loading = true;

        const previousLineItems = this.quote?.line_items;
        if (this.quote) {
            this.quote.line_items = this.quote.line_items.filter(item => item.uid !== item_uid);
        }

        requests
            .delete(`/customer/quote/${this.quote_uid}/remove-item/${item_uid}/`)
            .then(data => {
                this.setQuote(data);
            })
            .catch(err => {
                runInAction(() => {
                    // The part still exists on the server: show it again.
                    if (this.quote && previousLineItems) {
                        this.quote.line_items = previousLineItems;
                    }
                    this.loading = false;
                });
                message.error('Error, please try again');
            })
            .finally(() => {
                this.pendingDeletes.delete(item_uid);
            });
    }

    /**
     * Deletes every sibling line item that makes up a "multiple quantities"
     * variation (see `reconcileQuantities`) in sequence, keeping the quote in
     * sync with the last server response instead of racing parallel deletes.
     */
    async deleteLineItems(item_uids: Array<string>): Promise<void> {
        if (!item_uids.length) {
            return;
        }

        this.noteWrite();
        item_uids.forEach(uid => this.pendingDeletes.add(uid));
        this.loading = true;

        try {
            let data = null;
            for (const uid of item_uids) {
                data = await requests.delete(`/customer/quote/${this.quote_uid}/remove-item/${uid}/`);
            }
            if (data) {
                this.setQuote(data);
            } else {
                await this.getQuote();
            }
        } catch (err) {
            // A partial failure may have already deleted some siblings on the
            // server; refetch instead of trusting local state so the UI never
            // shows an item as removed when it still exists (or vice versa).
            await this.getQuote();
            message.error('Error, please try again');
        } finally {
            item_uids.forEach(uid => this.pendingDeletes.delete(uid));
            this.loading = false;
        }
    }

    deleteMasterProduct(master_product_id: number) {
        return requests
            .delete(`/customer/quote/${this.quote_uid}/remove-part/${master_product_id}/`)
            .then(data => {
                this.setQuote(data);
            })
            .catch(err => {
                message.error('Error, please try again');
            });
    }

    async deleteMasterProducts(master_product_ids: number[]): Promise<void> {
        this.noteWrite();
        if (!master_product_ids.length) {
            return;
        }

        try {
            const data = await requests.delete(`/customer/quote/${this.quote_uid}/remove-parts/`, {
                data: { part_ids: master_product_ids },
            });
            this.setQuote(data);
        } catch (err) {
            message.error('Error, please try again');
        }
    }

    deleteAll() {
        this.loading = true;

        requests
            .delete(`/customer/quote/${this.quote_uid}/remove-all/`)
            .then(data => {
                this.setQuote(data);
            })
            .catch(err => {
                this.setLoading(false);
                message.error('Error, please try again');
            });
    }

    newLineItem() {
        this.loading = true;

        const id = this.quote_uid;

        requests
            .post(`/customer/quote/${id}/new-item/`)
            .then(data => {
                runInAction(() => {
                    if (this.quote) {
                        this.quote.line_items.push(data);
                    }
                    this.getQuote();
                });
            })
            .catch(err => {
                this.setLoading(false);
                message.error('Error, please try again');
            });
    }

    exportToPdf() {
        return requests.get(`/customer/quote/${this.quote_uid}/export/`, {
            responseType: 'blob',
        });
    }

    sendToReview(payload = {}) {
        return requests.post(`/customer/quote/${this.quote_uid}/send-to-review/`, payload);
    }

    async getAllByAccountId(accountId: number, params?: any) {
        this.loading = true;
        try {
            const data = await get(`/staff/v2/quoted-products/`, {
                params: {
                    ...params,
                    quote__account: accountId,
                },
            });
            runInAction(() => {
                this.part_list_response = data;
            });
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    getTolerances() {
        requests
            .get(`/customer/part-tolerances`)
            .then(data => {
                runInAction(() => {
                    this.partTolerances = data.results;
                });
            })
            .catch(error => {
                console.error(error);
            });

        requests
            .get(`/customer/hole-tolerances`)
            .then(data => {
                runInAction(() => {
                    this.holeTolerances = data.results;
                });
            })
            .catch(error => {
                console.error(error);
            });
    }

    getFinishes() {
        requests
            .get(`/customer/finishes/`)
            .then(data => {
                runInAction(() => {
                    this.finishes = data.results;
                });
            })
            .catch(error => {
                console.error(error);
            });
    }

    getCoatings() {
        requests
            .get(`/customer/coatings/`)
            .then(data => {
                runInAction(() => {
                    this.coatings = data.results;
                });
            })
            .catch(error => {
                console.error(error);
            });
    }

    /** Coatings already loaded for the material, without requesting them. */
    getCachedCoatingsByMaterial(materialId: number): Array<CoatingsProps> | undefined {
        return this.coatingsByMaterial.peek(materialId);
    }

    /**
     * Coatings compatible with the material; callers should use the returned list, not `coatings`.
     * Returns null when the request failed, so callers do not mistake it for "none compatible".
     */
    async getCoatingsByMaterial(materialId: number): Promise<Array<CoatingsProps> | null> {
        const cached = this.coatingsByMaterial.peek(materialId) !== undefined;
        try {
            if (!cached) this.setLoading(true);
            // Returned only: `this.coatings` is the full catalog (bulk panels, errorFields), and
            // each part card keeps the list of its own material.
            return await this.coatingsByMaterial.get(materialId);
        } catch (error) {
            console.error('Error fetching coatings by material:', error);
            message.error('Failed to load compatible coatings');
            return null;
        } finally {
            if (!cached) {
                runInAction(() => {
                    this.setLoading(false);
                });
            }
        }
    }

    getMaterials() {
        requests
            .get(`/customer/materials/`)
            .then(data => {
                runInAction(() => {
                    this.materials = data.results;
                });
            })
            .catch(error => {
                console.error(error);
            });
    }

    getAdditionalRequirements = () => {
        requests.get(`/customer/additional-requirements/`).then(data => {
            runInAction(() => {
                this.additional_requirements = data.results;
            });
        });
    };

    getAccounts(params = {}) {
        this.loading_accounts = true;
        return requests
            .get(`/staff/accounts/`, {
                params: params,
            })
            .then(data => {
                runInAction(() => {
                    this.accounts = data.results;
                });
            })
            .finally(() => (this.loading_accounts = false));
    }

    getUsers(params = {}) {
        requests
            .get(`/staff/users/`, {
                params: params,
            })
            .then(data => {
                runInAction(() => {
                    this.users = data.results;
                });
            });
    }

    lineItems() {
        return (
            this.quote?.line_items?.filter(item => {
                const { completed, status } = item.product.analysis_status.file_3d;
                return status == 'SUCCESS' && completed;
            }) || []
        );
    }

    safeLineItems() {
        return (
            this.quote?.line_items?.filter(item => {
                const { completed, status } = item.product.analysis_status.file_3d;
                return status == 'SUCCESS' && completed && !item.needs_manual_quote;
            }) || []
        );
    }

    hasErrorFiles() {
        return (
            this.quote &&
            this.quote.line_items?.filter(item => {
                return (
                    (!item?.product?.material && !item?.product?.has_file_2d) ||
                    !item?.product?.finishes?.length ||
                    !item?.product?.coatings ||
                    (!item?.quantities && !this.isRequote)
                );
            })?.length > 0
        );
    }

    needsRFQ() {
        return (
            this.quote &&
            this.quote.line_items?.filter(item => {
                if (item.needs_manual_quote) {
                    return true;
                }
                if (item?.quote_action == 'manual_quote') {
                    return true;
                }
            })?.length > 0
        );
    }

    needs3DFiles() {
        return (
            this.quote &&
            this.quote.line_items?.filter(item => {
                return !item.product.has_file_3d;
            })?.length > 0
        );
    }

    needs2DFiles() {
        return (
            this.quote &&
            this.quote?.line_items?.filter(item => {
                return !item.product.has_file_2d;
            })?.length > 0
        );
    }

    setForStaff(for_staff: boolean) {
        this.for_staff = for_staff;
    }

    uploadAdditionalFile(file: File) {
        const formData = new FormData();

        formData.append('file', file);

        this.loading = true;

        return requests.post(`/customer/quote/${this.quote_uid}/upload-additional-file/`, formData).finally(() => {
            runInAction(() => {
                this.loading = false;
            });
        });
    }

    get errorFields() {
        if (this.quote && this.quote.line_items) {
            const catalogs = { materials: this.materials, finishes: this.finishes, coatings: this.coatings };
            return this.quote.line_items.flatMap((item: QuoteLineItemProps) =>
                getLineItemErrorFields(item, catalogs)
            ) as Array<ErrorField>;
        } else {
            return [];
        }
    }

    get isRequote() {
        return this.quote?.type === 'Requote';
    }

    get isNewContact() {
        return this.is_new_contact;
    }

    setIsNewContact(is_new: boolean) {
        return (this.is_new_contact = is_new);
    }

    setCurrentUser(user: UserProps) {
        return (this.current_user = user);
    }

    get getFirstName() {
        const firsName =
            ((this.quote && this.quote.contact?.first_name) || this.quote?.first_name) ??
            (LeadStore.lead?.first_name || '');
        return firsName;
    }

    get getLastName() {
        const lastName =
            ((this.quote && this.quote.contact?.last_name) || this.quote?.last_name) ??
            (LeadStore.lead?.last_name || '');
        return lastName;
    }

    get getEmail() {
        return (this.quote && this.quote.contact?.email) || this.quote?.email || '';
    }

    get getCompany() {
        const company =
            ((this.quote && this.quote.contact?.account) || this.quote?.company) ??
            (LeadStore.lead?.company || this.current_user?.company || '');

        return company;
    }

    get getPhone() {
        const phone = ((this.quote && this.quote.contact?.phone) || this.quote?.phone) ?? (LeadStore.lead?.phone || '');
        return phone;
    }

    get getZip() {
        return (this.quote && this.quote.contact?.zip_code) || this.quote?.shipping_zip_code || '';
    }

    get getTitle() {
        const title = (this.quote && this.quote.contact?.title) ?? (LeadStore.lead?.title || '');
        return title;
    }

    get getRelatedContact() {
        return (this.quote && this.quote.contact?.related_contact) || this.quote?.related_contact || '';
    }

    get getAdditionalFiles() {
        if (this.quote && this.quote.additional_files.length > 0) {
            const additional_files: Array<{
                uid: string;
                name: string;
                size: number;
                type: string;
            }> = [];
            this.quote.additional_files.map((file: { filename: string; id: number }) => {
                additional_files.push({
                    uid: file.id.toString(),
                    name: file.filename,
                    size: 0,
                    type: '',
                });
            });
            return additional_files;
        }
        return [];
    }

    updateContactInfo(value: boolean) {
        if (this.quote && this.quote.contact) {
            this.quote.contact = { ...this.quote.contact, sms_notifications: value };
        }
    }

    getContactDetails(contact_id: number) {
        requests.get(`/customer/contacts/${contact_id}/`).then(data => {
            runInAction(() => {
                this.contact = data;
            });
        });
    }

    getAdditionalRequirementsContactAccount(contact_id: number) {
        return requests
            .get(`/customer/contacts/${contact_id}/additional-requirements-documents/`)
            .then((data: { requirements: any[]; documents: any[] }) => {
                runInAction(() => {
                    this.default_documents = data.documents;
                    // Only the contact/account defaults: the requirements editor compares the quote against
                    // this list to tell customer defaults from requirements added to the quote.
                    this.default_requirements = data.requirements
                        ? data.requirements.map(r => ({
                              key: r.requirement.id,
                              label: r.requirement.name,
                              value: r.value,
                              files: r.files,
                          }))
                        : [];
                });
            });
    }

    async updateRequiredQuantities(id: number, payload: { quantities: number[] }) {
        const response = await post(`/staff/quoted-products/${id}/get_dynamic_quantities/`, payload);
        if (response) {
            runInAction(() => {
                this.getQuote();
            });
        }
    }

    /**
     * Add Quoted line items of a specific quote
     */
    async getQuoteLineItems(quote_id: string | number) {
        this.loading_quote_items = true;
        await get(`/customer/quotes/${quote_id}/line-items/`)
            .then(response => {
                this.previous_quoted_parts = response.results;
            })
            .finally(() => (this.loading_quote_items = false));
    }

    createLineItemFromExistingLineItems = async (line_items: number[]) => {
        return await post(`/customer/quote/${this.quote_uid}/add-existing-quoted-product/`, {
            quoted_products: line_items,
        }).finally(() => this.getQuote());
    };
    // rev
    searchPreviousParts = async (payload: any) => {
        this.loading_quote_items = true;
        await post(`/staff/master-products/parts-by-account/`, payload)
            .then(response => {
                this.previous_quoted_parts = response;
            })
            .finally(() => (this.loading_quote_items = false));
    };

    clean_previous_items() {
        this.loading_quote_items = false;
        this.previous_quoted_parts = [];
        this.entities = [];
        this.part_list_response = null;
    }

    /***
     * Use this option to update a field in the quote
     * @param key
     * @param value
     */
    async update_quote_field(key: string, value: any) {
        if (this.quote != undefined) {
            // this.quote = {
            //     ...this.quote,
            //     [key]: value,
            // };
            const payload = {
                [key]: value,
            };
            this.loading = true;
            await patch(`/customer/quote/${this.quote_uid}/`, payload).finally(() => {
                this.loading = false;
            });
            await this.getQuote();
        }
    }

    async update_quote_fields(payload: Record<string, unknown>) {
        if (this.quote != undefined) {
            this.loading = true;
            await patch(`/customer/quote/${this.quote_uid}/`, payload).finally(() => {
                this.loading = false;
            });
            await this.getQuote();
        }
    }

    async update_account_field(account_id: number, payload = {}): Promise<void> {
        // This is only for staff users
        await patch(`/staff/accounts/${account_id}/`, payload);
    }

    get hasBuyNow(): boolean {
        return !!this.quote?.line_items?.some(item => item?.quote_action === 'buy_now');
    }
    get hasManualQuote(): boolean {
        return !!this.quote?.line_items?.some(item => item?.quote_action === 'manual_quote');
    }

    /**
     * Applies a price option to a line item of the open quote. The URL carries the quote's
     * key, not its numeric id: a guest may only change prices with the signed uid of their
     * draft, and a signed in user with the quote's uid.
     */
    async updateItemPrice(payload: { id: number; price_id: string }): Promise<void> {
        this.noteWrite();
        const response = await patch(`/customer/quote/${this.quote_uid}/update-item-price/`, payload).then(data => {
            runInAction(() => {
                if (this.quote) {
                    // Replace only the priced item so the other parts do not re-render.
                    const index = this.quote.line_items.findIndex(item => item.uid === data.uid);
                    if (index !== -1) {
                        this.quote.line_items[index] = data;
                    }
                }

                // this.getQuote();
            });
        });

        return response;
    }

    // Temporary data for debugging safe auto quotes
    async getSessionUser() {
        await get(`/rest-auth/user/`).then(data => {
            runInAction(() => {
                this.current_user = data;
                this.user_is_staff = data.is_staff;
                this.user_is_tester = data.is_tester;
            });
        });
    }

    async reanalyzeMasterProduct(id: number): Promise<void> {
        try {
            if (this.user_is_staff) {
                await requests.post(`/staff/master-products/${id}/analyze/`);
            }
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Failed to start analysis');
        }
    }
}

const quoteStore = new QuoteStore();

export default quoteStore;
